import React, { useMemo } from 'react';
import { clone } from 'three/examples/jsm/utils/SkeletonUtils.js';
import { KTX2Loader } from 'three-stdlib';
import { useThree } from '@react-three/fiber';
import { useGLTF, useAnimations } from '@react-three/drei';
import { ASSETS_2022_CDN } from 'util/constants';
import * as THREE from 'three';
import { getTexture } from '@src/components/scene/canvas-html/editorDrawer/objectTab/changeTextureComponent';

const urlAnimationIdle = `${ASSETS_2022_CDN}/Global/Avatares2025/AI/Animations/Idle.gltf`;
const DEFAULT_POSITION: [number, number, number] = [0, 0, 0];
const ktx2Loader = new KTX2Loader();
ktx2Loader.setTranscoderPath(
  `https://unpkg.com/three@0.168.0/examples/jsm/libs/basis/`
);

type Props = {
  url: string;
  children?: any;
  position?: THREE.Vector3 | undefined;
  scale?: THREE.Vector3 | undefined;
  rotation?: THREE.Euler | undefined;
  name: string;
  refer?: any;
  dispose?: any;
  castShadow?: boolean;
  receiveShadow?: boolean;
  textureToChange?: string;
  /**
   * prop to check if the avatar is an IA avatar and use the animation idle
   */
  isIAAvatar?: boolean;
  /**
   * prop to check if the avatar is a NPC avatar and use the animation idle
   */
  isNPCAvatar?: boolean;
  isUploadedAsset?: boolean;
  showOnMinimap?: boolean;
};

const Model: React.FC<Props> = ({
  url,
  children,
  position,
  scale,
  rotation,
  name,
  refer,
  dispose,
  receiveShadow,
  castShadow,
  textureToChange,
  isIAAvatar = false,
  isNPCAvatar = false,
  isUploadedAsset = false,
  showOnMinimap = false,
}) => {
  const { gl } = useThree();
  const { scene, animations }: any = useGLTF(url, true, true, (loader) => {
    loader.setKTX2Loader(ktx2Loader.detectSupport(gl));
  });

  const { animations: animationAvatars }: any = useGLTF(
    urlAnimationIdle,
    true,
    true
  );
  const { actions } = useAnimations(
    !isIAAvatar && !isNPCAvatar ? animations : animationAvatars,
    refer
  );

  React.useEffect(() => {
    if (scene) {
      scene.animations = animations;
      refer.current.animations = animations;
      const maxAnisotropy = gl?.capabilities?.getMaxAnisotropy() || 1;

      if (actions && refer.current) {
        refer.current.actions = actions;
      }
      let texture;
      if (textureToChange) {
        texture = getTexture(textureToChange);
      }
      scene.textureToChange = textureToChange || '';
      refer.current.textureToChange = textureToChange || '';

      if ((isIAAvatar || isNPCAvatar) && refer.current) {
        refer.current.name = name;
      }

      scene.traverse((child: any) => {
        if (child.isMesh) {
          child.castShadow = true;
          child.receiveShadow = true;

          let material = child.material;

          if (texture) {
            material = child.material.clone();
            material.map = texture;
            material.needsUpdate = true;
          }
          child.material = material;

          material.depthWrite = !material.transparent;
          material.skinning = true;
          const encoding = (THREE as any).SRGBColorSpace;
          if (material.map) {
            material.map.minFilter = THREE.LinearMipmapLinearFilter;
            material.map.anisotropy = maxAnisotropy;
            material.map.colorSpace = encoding;
          }
          if (material.emissiveMap) {
            material.emissiveMap.anisotropy = maxAnisotropy;
            material.emissiveMap.colorSpace = encoding;
            material.toneMapped = false;
            material.emissiveIntensity += 1; //add an offset to current emissive intensity
          } else {
            child.material.emissiveIntensity = 0;
          }

          material.envMapIntensity = 0.25;

          material.needsUpdate = true;
        }
      });
    }
  }, [scene]);

  const modelGroup = useMemo(() => {
    const cloned = clone(scene); // deep clone with skeleton + animations preserved
    cloned.name = 'ModelRoot';
    return cloned;
  }, [scene]);

  return (
    <>
      <primitive
        ref={refer}
        object={modelGroup}
        key={name}
        position={position ? position : DEFAULT_POSITION}
        scale={scale ? scale : [1, 1, 1]}
        rotation={rotation}
        name={name}
      />
    </>
  );
};

export default Model;
